import '@/styles/globals.css'

import type { AppProps } from 'next/app'
import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { appWithTranslation, useTranslation } from 'next-i18next/pages'
import type { ThemeProviderProps } from 'next-themes'
import { ThemeProvider as NextThemeProvider } from 'next-themes'
import React, { useEffect, useLayoutEffect, useState } from 'react'
import { CookieConsent } from 'react-cookie-consent'
import { Provider } from 'react-redux'
import { acceptTracking, denyTracking, initTracking } from 'track'
import { AppControls } from '@/components/AppControls'
import { Banner } from '@/components/Banner'
import LocaleMenu, { locales } from '@/components/LocaleMenu'
import TopHeader from '@/components/TopHeader'
import i18nextConfig from '@/next-i18next.config'
import { config } from '@/site.config.js'
import { persistor, store } from '@/state/store'
import { installPlaywrightI18nGuard } from '@/utils/playwrightI18nGuard'
import { getSplitTestVariant } from '@/utils/splitTest'
import languageDetector from '../utils/languageDetector'

const COOKIE_NAME = 'pcl-allow-cookies'
const GA_TRACKING_ID = 'G-3G2KQMVJR0'

// tsgo doesn't resolve `children` from `extends React.PropsWithChildren` in JSX (microsoft/typescript-go#1090)
const ThemeProvider = NextThemeProvider as (
  props: ThemeProviderProps & { children?: React.ReactNode },
) => React.ReactElement

// useLayoutEffect fires synchronously before first paint on the client,
// avoiding a flash of missing consent banner in GDPR locales. During
// SSR (static build) it falls back to useEffect (a no-op at build time).
const useBrowserLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect

// Feature flags defined via Next.js env config at build time for Turbopack compatibility

function App({ Component, pageProps }: AppProps) {
  // Starts false (matches static HTML); set synchronously before first paint
  // by reading the data-needs-consent attribute written by the inline script
  // in _document.tsx.
  const [showConsent, setShowConsent] = useState(false)

  useBrowserLayoutEffect(() => {
    if (document.documentElement.hasAttribute('data-needs-consent')) {
      setShowConsent(true)
    }
  }, [])

  const router = useRouter()
  const activeLocale =
    typeof router.query.locale === 'string'
      ? router.query.locale
      : i18nextConfig.i18n.defaultLocale

  // Static root HTML is English. When the client redirects a visitor to a
  // cached locale, keep the document language aligned with the new route for
  // screen readers and other assistive technology.
  useBrowserLayoutEffect(() => {
    document.documentElement.lang = activeLocale
  }, [activeLocale])

  const isAppPath =
    router.pathname === '/' ||
    router.pathname === '/[locale]' ||
    router.pathname === '/[locale]?*' ||
    router.pathname === '/?*' ||
    router.pathname === '/[locale]/list/[slug]'
  const translation = useTranslation()
  installPlaywrightI18nGuard(translation.i18n, {
    sourceLocale: i18nextConfig.i18n.defaultLocale,
    sourceOnlyNamespaces: i18nextConfig.sourceOnlyNamespaces,
  })
  const { t } = translation
  const termsHref = `/${
    router.query.locale &&
    router.query.locale !== i18nextConfig.i18n.defaultLocale
      ? router.query.locale
      : i18nextConfig.i18n.defaultLocale
  }/terms-of-service`
  useEffect(() => {
    persistor.persist()
    const environment = window.location.href.startsWith(config.prodDomain)
      ? 'production'
      : 'development'
    if (environment === 'production') {
      initTracking({ gaTrackingId: GA_TRACKING_ID, cookieName: COOKIE_NAME })
    }
    import('@sentry/react').then((Sentry) => {
      Sentry.init({
        dsn: 'https://89863ee5d6a640418e61b806e25797aa@o78809.ingest.us.sentry.io/6431694',
        integrations: [
          Sentry.browserTracingIntegration({ enableInp: true }),
          Sentry.httpClientIntegration({
            failedRequestStatusCodes: [[500, 599]],
            failedRequestTargets: [/\/\.netlify\/functions\/generateReasons$/],
          }),
          Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
        ],
        environment,
        release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
        tracePropagationTargets: [/^\/\.netlify\/functions\/generateReasons$/],
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 0.05,
        replaysOnErrorSampleRate: 1.0,
        beforeSend(event) {
          // A client can retain an older Next.js build across a deploy. A
          // prefetch for that retired build ID correctly 404, so discard only
          // Sentry's automatic event when its stack identifies a prefetch.
          const isStaleNextDataPrefetch =
            event.request?.url?.includes('/_next/data/') &&
            event.exception?.values?.some(
              ({ mechanism, stacktrace, value }) =>
                mechanism?.type?.startsWith('auto.http.client.') &&
                value === 'HTTP Client Error with status code: 404' &&
                stacktrace?.frames?.some(({ function: functionName }) =>
                  functionName?.includes('prefetch'),
                ),
            )
          if (isStaleNextDataPrefetch) return null

          delete event.extra
          if (event.request) {
            delete event.request.data
          }
          return event
        },
      })
      Sentry.setTag('Branch', getSplitTestVariant())
      Sentry.setTag(
        'locale',
        (router.query.locale as string | undefined) ??
          i18nextConfig.i18n.defaultLocale,
      )
    })
  }, [])

  // Get the current path segments
  const pathSegments = router.asPath.split('/')

  // Remove the first segment if it's a locale
  if (locales.map((locale) => locale[0]).includes(pathSegments[1])) {
    pathSegments.splice(1, 1)
  }

  // Join the segments back together to get the path without the locale
  const pathWithoutLocale = pathSegments.join('/').replace(/\/$/, '')

  return (
    <React.StrictMode>
      <ThemeProvider attribute="class">
        <Provider store={store}>
          <div className="App flex flex-col min-h-screen pb-16 min-[1400px]:pb-0">
            <Head>
              <meta charSet="utf-8" />
              <link rel="icon" href="/favicon.ico" sizes="any" />
              <meta name="keywords" content={t('keywords')} />
              <meta
                name="viewport"
                content="width=device-width, initial-scale=1"
              />
              {locales.map((locale) => {
                let href = `${config.prodDomain}/${locale[0]}${pathWithoutLocale}`
                // pathWithoutLocale is empty AND locale is the default EN, then we don't need to add the locale to the href
                if (
                  pathWithoutLocale === '' &&
                  locale[0] === i18nextConfig.i18n.defaultLocale
                ) {
                  href = `${config.prodDomain}`
                }
                return (
                  <link
                    key={locale[0]}
                    rel="alternate"
                    href={href}
                    hrefLang={locale[0]}
                  />
                )
              })}
            </Head>
            <header role="banner">
              <TopHeader isAppPath={isAppPath} />
            </header>
            <main role="main">
              {isAppPath && <AppControls />}
              <Component {...pageProps} locale={languageDetector.detect()} />
            </main>

            <footer
              className="sticky top-[100vh] flex-none py-6 px-2 self-center text-center md:text-left md:self-start text-xs md:w-full"
              role="contentinfo"
            >
              {showConsent && (
                <CookieConsent
                  enableDeclineButton
                  onDecline={denyTracking}
                  cookieName={COOKIE_NAME}
                  buttonText={t('CookieConsent.accept')}
                  declineButtonText={t('CookieConsent.decline')}
                  ariaAcceptLabel={t('CookieConsent.accept')}
                  ariaDeclineLabel={t('CookieConsent.decline')}
                  declineButtonStyle={{ backgroundColor: '#C11535' }}
                  style={{ fontSize: '.8em' }}
                  containerClasses={'test-cookie-consent'}
                  buttonStyle={{ backgroundColor: '#3ab56b' }}
                  onAccept={acceptTracking}
                >
                  {t('CookieConsent.message')}
                </CookieConsent>
              )}
              <div className="inline-block mx-2">
                <LocaleMenu bottom={true} />
              </div>
              <span className={"sm:after:content-['|'] block sm:inline"}>
                &copy; {new Date().getFullYear()}{' '}
              </span>{' '}
              <Link href={termsHref} prefetch={false}>
                {t('Footer.termsLinkTitle')}
              </Link>
            </footer>
            {process.env.FEATURES_SHOW_PDF_BANNER === 'true' && <Banner />}
          </div>
        </Provider>
      </ThemeProvider>
    </React.StrictMode>
  )
}

export default appWithTranslation(App, i18nextConfig)
